import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validatorsv2.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import { SingleHorizontalOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Input } from '@connected/ui/components/input';
import { Popout, PopoutBody, PopoutContent, PopoutFooter, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Separator } from '@connected/ui/components/separator';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { cn } from '@connected/ui/lib/utils';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Pen, Trash, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CheckList } from '@/components/checklist';
import { ContactPersonsList } from '@/components/contact-persons';
import { DataTable } from '@/components/data-table';
import { InformationLine } from '@/components/information-line';
import { SearchOrganization } from '@/components/kvk-search';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useFormTouched } from '@/hooks/use-form-touched';
import { queryClient } from '@/lib/query-client';
import { booleanDefaultValue, getFormErrors, placeFormErrors } from '@/lib/utils';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/controlpoints/',
)({
    component: RouteComponent,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
            electricalProjectVersionId: params.projectVersionId,
        }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
    },
});

type InspectionFormValues = {
    isPVInstallationEquipotentialBondingPassed: boolean | null;
};

function RouteComponent() {
    const { t } = useTranslation('re-inspections');
    const params = Route.useParams();

    return (
        <>
            <Vereffening />

            <CheckList
                title={t('controlpoints.checklists.constructionOffice')}
                kind="Scope12RoofConstructionCalculation"
                projectVersionId={params.projectVersionId}
                before={<ContructionCompany />}
            />

            <CheckList
                title={t('controlpoints.checklists.visualInspection')}
                kind="Scope12VisualInspection"
                projectVersionId={params.projectVersionId}
            />

            <CheckList
                title={t('controlpoints.checklists.cablePath')}
                kind="Scope12PVInstallationPowerLine"
                projectVersionId={params.projectVersionId}
            />

            <CheckList
                title={t('controlpoints.checklists.additionalControlPoints')}
                kind="Scope12AdditionalInspectionPoints"
                projectVersionId={params.projectVersionId}
            />
            <CheckList
                title={t('controlpoints.checklists.documentationDCSide')}
                kind="Scope12DocumentationDCSide"
                projectVersionId={params.projectVersionId}
            />
            <CheckList
                title={t('controlpoints.checklists.manualsAndDataSpecifications')}
                kind="Scope12ManualAndDataSpecifications"
                projectVersionId={params.projectVersionId}
            />
            <CheckList
                title={t('controlpoints.checklists.generalRemarks')}
                kind="Scope12GeneralRemarks"
                projectVersionId={params.projectVersionId}
            />
            <CheckList
                title={t('controlpoints.checklists.documentationACSide')}
                kind="Scope12DocumentationACSide"
                projectVersionId={params.projectVersionId}
            />
            <CheckList
                title={t('controlpoints.checklists.measurementAndTesting')}
                kind="Scope12MeasurementsAndTesting"
                projectVersionId={params.projectVersionId}
            />
            <CheckList
                title={t('controlpoints.checklists.testingOtherParts')}
                kind="Scope12VisualInspectionOtherParts"
                projectVersionId={params.projectVersionId}
                after={<Footer />}
            />
        </>
    );
}

function Vereffening() {
    const params = Route.useParams();
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    const measurementQuery = useQuery(elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
        electricalProjectVersionId: params.projectVersionId,
    }));

    const { data } = useQuery(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
        electricalProjectVersionId: params.projectVersionId,
    }));

    /// api/v2/electrical/projects/inspections/edit

    const form = useForm<InspectionFormValues, ZodValidator>({
        defaultValues: {
            isPVInstallationEquipotentialBondingPassed: booleanDefaultValue(data?.Inspection?.IsPVInstallationEquipotentialBondingPassed),
        },
        validatorAdapter: zodValidator(),
    });

    const { patchField } = usePatcher('/api/v2/electrical/projects/inspections/patch', {
        scope: {
            id: params.projectVersionId,
        },
        onMutate: async (variable) => {
            const body = toPascalCase(variable?.body)!;

            const queryKey = elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
                electricalProjectVersionId: params.projectVersionId,
            }).queryKey;

            const previousData = queryClient.getQueryData<components['schemas']['EditElectricalProjectInspectionQueryResult']>(queryKey);

            const newData = {
                ...previousData,
                Inspection: {
                    ...previousData?.Inspection,
                    ...body,
                },
            };

            queryClient.setQueryData<components['schemas']['EditElectricalProjectInspectionQueryResult']>(
                queryKey,
                newData,
            );
            return { previousData };
        },
    }, {
        body: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });

    const columns = useMemo<ColumnDef<components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResultMeasurement']>[]>(() => ([
        {
            accessorKey: 'Location',
            header: t('controlpoints.table.columns.location'),
            cell: ({ row }) => {
                if (row.original.Location)
                    return row.original.Location;
                return t('controlpoints.table.noLocation');
            },
        },
        {
            accessorKey: 'Resistance',
            header: t('controlpoints.table.columns.value'),
            cell: ({ row }) => {
                if (row.original.Resistance) {
                    return `${row.original.Resistance} Ω`;
                }
                return t('controlpoints.table.noValue');
            },
        },
        {
            accessorKey: 'EditButton',
            header: '',
            cell: ({ row }) => (
                <div className="flex flex-row justify-end">
                    <EBMeasurement item={row.original} />
                </div>
            ),
        },
        {
            accessorKey: 'DeleteButton',
            header: '',
            cell: ({ row }) => (
                <div className="flex flex-row justify-end">
                    <DeleteMeasurementButton
                        id={row.original.ElectricalProjectEquipotentialBondingMeasurementId!}
                    />
                </div>
            ),
        },
    ]), [t]);

    const tableColumns = useMemo(
        () =>
            measurementQuery.isLoading
                ? columns.map(column => ({
                        ...column,
                        cell: () => (
                            <Skeleton className="h-8 w-full" />
                        ),
                    }))
                : columns,
        [columns, measurementQuery.isLoading],
    );

    const tableData = useMemo<components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResultMeasurement'][]>(
        () => {
            if (measurementQuery.isError) {
                toast.error(measurementQuery.error?.Message);
                return [];
            }

            if (!measurementQuery.data) {
                return [];
            }

            return measurementQuery.data.Measurements;
        },
        [measurementQuery.isError, measurementQuery.data, measurementQuery.error?.Message],
    );

    return (
        <Section>
            <Section.Header>
                <Section.Header.Title>{t('controlpoints.title')}</Section.Header.Title>
            </Section.Header>
            <Section.Content>
                <form.Field
                    name="isPVInstallationEquipotentialBondingPassed"
                    validators={{
                        onChange: validators.editElectricalProjectInspectionCommand.isPVInstallationEquipotentialBondingPassed,
                        onChangeAsync: patchField('isPVInstallationEquipotentialBondingPassed'),
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('controlpoints.form.measurementLabel')}
                        >
                            <SingleOptionField
                                className="flex flex-row w-full"
                                name={name}
                                handleChange={handleChange}
                                state={state}
                                options={[
                                    {
                                        label: c('words.yes'),
                                        value: true,
                                    },
                                    {
                                        label: c('words.no'),
                                        value: false,
                                    },
                                ]}
                                render={SingleHorizontalOptionItem}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>

                <DataTable
                    className="bg-background-secondary"
                    columns={tableColumns}
                    data={tableData}
                    cellClassName={cell => cn('', {
                        'w-[2rem] px-1': ['DeleteButton', 'EditButton'].includes(cell.column.id),
                    })}
                />
                <EBMeasurement item={null} />
            </Section.Content>
        </Section>
    );
}

type EBMeasurementFormValues = {
    location: string;
    resistance: number | null;
};

function EBMeasurement({
    item,
}: {
    item: components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResultMeasurement'] | null;
}) {
    const params = Route.useParams();
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    const [isOpen, setIsOpen] = useState(false);

    const ebMeasurementCreateMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/ebmeasurements/create',
        {
            onMutate: (variables) => {
                const body = toPascalCase(variables?.body)!;

                const ebMeasurementQueryKey = elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
                    electricalProjectVersionId: params.projectVersionId,
                });

                const previousData = queryClient.getQueryData(ebMeasurementQueryKey.queryKey) as components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResult'];

                const newMeasurement: components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResultMeasurement'] = {
                    ElectricalProjectEquipotentialBondingMeasurementId: body.ElectricalProjectEquipotentialBondingMeasurementId,
                    Location: body.Location,
                    Resistance: body.Resistance,
                };

                const newData: components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResult'] = {
                    ...previousData,
                    Measurements: [
                        ...(previousData.Measurements || []),
                        newMeasurement,
                    ],
                };

                queryClient.setQueryData(ebMeasurementQueryKey.queryKey, newData);
                return {
                    previousData,
                    newMeasurement,
                    ebMeasurementQueryKey,
                    newData,
                };
            },
            onSuccess: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
                        electricalProjectVersionId: params.projectVersionId,
                    }),
                );
            },
            onError: (error) => {
                const errors = getFormErrors<EBMeasurementFormValues>(error);
                if (errors.length > 0) {
                    // eslint-disable-next-line ts/no-use-before-define
                    placeFormErrors<EBMeasurementFormValues>({ fieldErrors: errors, formApi: form });
                }
                console.error('Error creating contact person:', errors);
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    const ebMeasurementEditMutation = api.useMutation(
        'put',
        '/api/v2/electrical/projects/ebmeasurements/edit',
        {
            onMutate: (variables) => {
                const body = toPascalCase(variables?.body)!;

                const ebMeasurementQueryKey = elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
                    electricalProjectVersionId: params.projectVersionId,
                });

                const previousData = queryClient.getQueryData(ebMeasurementQueryKey.queryKey) as components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResult'];

                const newData: components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResult'] = {
                    ...previousData,
                    Measurements: previousData.Measurements.map((measurement) => {
                        if (measurement.ElectricalProjectEquipotentialBondingMeasurementId === body.ElectricalProjectEquipotentialBondingMeasurementId) {
                            return {
                                ...measurement,
                                Location: body.Location,
                                Resistance: body.Resistance,
                            };
                        }
                        return measurement;
                    }),
                };

                queryClient.setQueryData(ebMeasurementQueryKey.queryKey, newData);
                return {
                    previousData,
                    ebMeasurementQueryKey,
                    newData,
                };
            },
            onSuccess: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
                        electricalProjectVersionId: params.projectVersionId,
                    }),
                );
            },
            onError: (error) => {
                const errors = getFormErrors<EBMeasurementFormValues>(error);
                if (errors.length > 0) {
                    // eslint-disable-next-line ts/no-use-before-define
                    placeFormErrors<EBMeasurementFormValues>({ fieldErrors: errors, formApi: form });
                }
                console.error('Error editing contact person:', errors);
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    const defaultValues: EBMeasurementFormValues = {
        location: item?.Location || '',
        resistance: item?.Resistance || null,
    };

    const form = useForm<EBMeasurementFormValues, ZodValidator>(
        {
            defaultValues,
            validatorAdapter: zodValidator(),
            onSubmit: ({ value }) => {
                if (!item) {
                    ebMeasurementCreateMutation.mutate({
                        body: {
                            ElectricalProjectVersionId: params.projectVersionId,
                            ElectricalProjectEquipotentialBondingMeasurementId: crypto.randomUUID(),
                            Location: value.location,
                            Resistance: value.resistance,
                        },
                    });
                }
                else {
                    ebMeasurementEditMutation.mutate({
                        body: {
                            ElectricalProjectEquipotentialBondingMeasurementId: item.ElectricalProjectEquipotentialBondingMeasurementId!,
                            Location: value.location,
                            Resistance: value.resistance,
                            Order: item.Order!,
                        },
                    });
                }
                if (form.state.errors.length === 0) {
                    setIsOpen(false);
                    form.reset();
                    ebMeasurementCreateMutation.reset();
                    ebMeasurementEditMutation.reset();
                }
            },
        },
    );

    const isFormTouched = useFormTouched(form.store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(c('messages.confirmClose'))) {
                setIsOpen(false);
                form.reset();
                ebMeasurementCreateMutation.reset();
                ebMeasurementEditMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            form.reset();
            ebMeasurementCreateMutation.reset();
            ebMeasurementEditMutation.reset();
        }
    }

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (

        <Popout open={isOpen} onOpenChange={setIsOpen}>
            {
                item
                    ? (
                            <Button variant="ghost" onClick={() => setIsOpen(true)}><Pen className="h-4 w-4" /></Button>
                        )
                    : (
                            <Button variant="link" onClick={() => setIsOpen(true)}>{t('controlpoints.modal.addMeasurement')}</Button>
                        )
            }
            <PopoutContent asChild>
                <form onSubmit={handleSubmit}>
                    <PopoutHeader>
                        <PopoutTitle>
                            {item ? t('controlpoints.modal.editTitle') : t('controlpoints.modal.addTitle')}
                        </PopoutTitle>
                    </PopoutHeader>
                    <PopoutBody>
                        <div
                            className="flex flex-col gap-4"
                        >
                            <form.Field
                                name="location"
                                validators={{
                                    onChange:
                                        validators.createElectricalProjectEquipotentialBondingMeasurementCommand.location,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t('controlpoints.form.location.label')}
                                    >
                                        <Input
                                            name={name}
                                            onChange={e =>
                                                handleChange(e.target.value)}
                                            value={state.value}
                                            placeholder={t('controlpoints.form.location.placeholder')}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </form.Field>
                            <form.Field
                                name="resistance"
                                validators={{
                                    onChange:
                                        validators.createElectricalProjectEquipotentialBondingMeasurementCommand.resistance,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t('controlpoints.form.resistance.label')}
                                    >
                                        <NumberField
                                            name={name}
                                            onChange={handleChange}
                                            value={state.value}
                                            placeholder={t('controlpoints.form.resistance.placeholder')}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </form.Field>
                        </div>
                    </PopoutBody>
                    <PopoutFooter>
                        <div className="flex w-full flex-col gap-4">
                            <div className="flex w-full gap-4">
                                <Button className="w-full" variant="outline" onClick={handleClose}>{c('actions.general.cancel_short')}</Button>
                                <Button className="w-full" type="submit">{c('actions.general.save')}</Button>
                            </div>
                        </div>
                    </PopoutFooter>
                </form>
            </PopoutContent>
        </Popout>
    );
}

function DeleteMeasurementButton({ id }: { id: string }) {
    const params = Route.useParams();
    const { t } = useTranslation('re-inspections');

    const deleteMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/ebmeasurements/delete',
        {
            onMutate: (variables) => {
                const ebMeasurementQueryKey = elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
                    electricalProjectVersionId: params.projectVersionId,
                });

                const previousData = queryClient.getQueryData(ebMeasurementQueryKey.queryKey) as components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResult'];
                const newData: components['schemas']['ListElectricalProjectEquipotentialBondingMeasurementQueryResult'] = {
                    ...previousData,
                    Measurements: previousData.Measurements.filter(
                        measurement => measurement.ElectricalProjectEquipotentialBondingMeasurementId !== variables.params.query.ElectricalProjectEquipotentialBondingMeasurementId,
                    ),
                };

                queryClient.setQueryData(ebMeasurementQueryKey.queryKey, newData);

                return {
                    previousData,
                    ebMeasurementQueryKey,
                };
            },
            onSuccess: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.controlPoints.ebMeasurementEditList({
                        electricalProjectVersionId: params.projectVersionId,
                    }),
                );
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={async () => {
                if (await showConfirm(t('controlpoints.messages.confirmDelete'))) {
                    await deleteMutation.mutateAsync({
                        params: {
                            query: {
                                ElectricalProjectEquipotentialBondingMeasurementId: id,
                            },
                        },
                    });
                }
            }}
        >
            <Trash className="h-4 w-4" />
        </Button>
    );
}

type ConstructionCompanyFormValues = {
    isPVInstallationConstructionCompanyKnown: boolean | null;
    pvInstallationConstructionDocumentNumber: string;
};

function ContructionCompany() {
    const { t } = useTranslation('re-inspections');
    const params = Route.useParams();

    const { data } = useQuery(
        elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
            electricalProjectVersionId: params.projectVersionId,
        }),
    );

    const { data: organisationData } = useQuery(
        elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
    );

    const currentOrganisation = organisationData?.Organizations?.filter(
        x => x.IsPVInstallationConstructionCompany,
    )[0];

    const constructionCompanyForm = useForm<ConstructionCompanyFormValues, ZodValidator>({
        defaultValues: {
            isPVInstallationConstructionCompanyKnown: data?.Inspection?.IsPVInstallationConstructionCompanyKnown ?? null,
            pvInstallationConstructionDocumentNumber: data?.Inspection?.PVInstallationConstructionDocumentNumber ?? '',
        },
        validatorAdapter: zodValidator(),
    });

    const { patchField } = usePatcher('/api/v2/electrical/projects/inspections/patch', {
        scope: {
            id: params.projectVersionId,
        },
        onMutate: async (variable) => {
            const body = toPascalCase(variable?.body)!;

            const queryKey = elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
                electricalProjectVersionId: params.projectVersionId,
            }).queryKey;

            const previousData = queryClient.getQueryData<components['schemas']['EditElectricalProjectInspectionQueryResult']>(queryKey);

            const newData = {
                ...previousData,
                Inspection: {
                    ...previousData?.Inspection,
                    ...body,
                },
            };

            queryClient.setQueryData<components['schemas']['EditElectricalProjectInspectionQueryResult']>(
                queryKey,
                newData,
            );
            return { previousData };
        },
    }, {
        body: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });

    return (
        <>
            <div className="flex flex-col gap-2">
                <constructionCompanyForm.Field
                    name="isPVInstallationConstructionCompanyKnown"
                    validators={{
                        onChange: validators.editElectricalProjectInspectionCommand.isPVInstallationConstructionCompanyKnown,
                        onChangeAsync: patchField('isPVInstallationConstructionCompanyKnown'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('controlpoints.constructionOffice.label')}
                        >
                            <SingleOptionField
                                className="flex flex-row w-full"
                                name={name}
                                handleChange={handleChange}
                                state={state}
                                options={[
                                    {
                                        label: 'Ja',
                                        value: true,
                                    },
                                    {
                                        label: 'Nee',
                                        value: false,
                                    },
                                ]}
                                render={SingleHorizontalOptionItem}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </constructionCompanyForm.Field>
                <constructionCompanyForm.Subscribe
                    selector={state => state.values.isPVInstallationConstructionCompanyKnown}
                >
                    {isPVInstallationConstructionCompanyKnown => (
                        isPVInstallationConstructionCompanyKnown
                        && (
                            <>
                                <constructionCompanyForm.Field
                                    name="pvInstallationConstructionDocumentNumber"
                                    validators={{
                                        onChange: validators.editElectricalProjectInspectionCommand.pVInstallationConstructionDocumentNumber,
                                        onChangeAsync: patchField('PVInstallationConstructionDocumentNumber'),
                                        onChangeAsyncDebounceMs: patchField.debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={t('controlpoints.constructionOffice.documentNumber.label')}
                                        >
                                            <Input
                                                name={name}
                                                onChange={e => handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t('controlpoints.constructionOffice.documentNumber.placeholder')}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </constructionCompanyForm.Field>

                                {currentOrganisation
                                    ? (
                                            <OrganizationInfo organization={currentOrganisation} />
                                        )
                                    : (
                                            <SearchOrganization
                                                params={params}
                                                organisationRoles={{
                                                    isProjectCustomer: false,
                                                    isObjectAdministrator: false,
                                                    isObjectOwner: false,
                                                    isObjectTenant: false,
                                                    isPVInstallationConstructionCompany: true,
                                                }}
                                            />
                                        )}

                            </>
                        )

                    )}
                </constructionCompanyForm.Subscribe>
            </div>
            <Separator />
        </>
    );
}

type ContactPerson = {
    firstName: string;
    middleName: string;
    lastName: string;
    department: string;
    jobTitleDescription: string;
    email: string;
    mobilePhoneNumber: string;
    roleInSoftware: string[];
};

type FormValues = {
    name: string;
    email: string;
    telephoneNumber: string;
    relationId: string;
    contactPersons: ContactPerson[];
};

type Organization = components['schemas']['ListElectricalProjectOrganizationQueryResultOrganization'];

function OrganizationInfo({ organization }: { organization: Organization }) {
    const { t } = useTranslation('applications');
    const { t: tReInspections } = useTranslation('re-inspections');

    const params = Route.useParams();

    const deleteOrganizationMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/organizations/delete',
        {
            onMutate: async (variables) => {
                const queryKey = elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId });

                await queryClient.cancelQueries(queryKey);

                const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(queryKey.queryKey);

                const newData = {
                    ...previousData,
                    Organizations: previousData?.Organizations?.filter(
                        org => org.ElectricalProjectOrganizationId !== variables.params.query.ElectricalProjectOrganizationId,
                    ) || [],
                };

                queryClient.setQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(
                    queryKey.queryKey,
                    newData,
                );
                return { previousData, queryKey, newData };
            },
            onSuccess: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
                );
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    const { Field } = useForm<FormValues, ZodValidator>({
        defaultValues: {
            name: organization?.Name || '',
            email: organization?.Email || '',
            telephoneNumber: organization?.TelephoneNumber || '',
            relationId: 'TODO',
            contactPersons: [],
        },
        validatorAdapter: zodValidator(),
    });

    const { patchField } = usePatcher(
        '/api/v2/electrical/projects/organizations/patch',
        {
            scope: {
                id: params.projectVersionId,
            },
            onMutate: async (variable) => {
                const body = toPascalCase(variable?.body)!;

                const queryKey = elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }).queryKey;

                const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(queryKey);

                const newData: components['schemas']['ListElectricalProjectOrganizationQueryResult'] = {
                    ...previousData,
                    Organizations: previousData?.Organizations?.map((org) => {
                        if (org.ElectricalProjectOrganizationId === body.ElectricalProjectOrganizationId) {
                            return {
                                ...org,
                                ...body,
                            };
                        }
                        return org;
                    }) || [],
                };

                queryClient.setQueryData<components['schemas']['ListElectricalProjectOrganizationQueryResult']>(
                    queryKey,
                    newData,
                );
                return { previousData, queryKey, newData };
            },
            onSuccess: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
                );
            },
        },
        { body: { ElectricalProjectOrganizationId: organization.ElectricalProjectOrganizationId } },
    );

    async function handleDelete() {
        if (await showConfirm('Weet je zeker dat je het constructiebureau wilt verwijderen?')) {
            deleteOrganizationMutation.mutate({
                params: {
                    query: {
                        ElectricalProjectOrganizationId:
                            organization.ElectricalProjectOrganizationId,
                    },
                },
            });
            deleteOrganizationMutation.reset();
        }
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div>
                    <Container.Title>{organization?.Name}</Container.Title>
                    <p className="text-text-tertiary">{tReInspections('controlpoints.checklists.constructionOffice')}</p>
                </div>
                <Button variant="ghost" onClick={handleDelete}>
                    <X />
                </Button>
            </div>
            <Separator />
            <div className="flex flex-col gap-2">
                <InformationLine
                    label={tReInspections('controlpoints.constructionOffice.kvkNumber.label')}
                    value={organization?.BusinessRegistration?.Number}
                />
                {organization?.BusinessRegistration?.BranchNumber && (
                    <InformationLine
                        label={tReInspections('controlpoints.constructionOffice.branchNumber.label')}
                        value={organization?.BusinessRegistration?.BranchNumber}
                    />
                )}
                <InformationLine
                    label={tReInspections('controlpoints.constructionOffice.streetAndNumber.label')}
                    value={
                        `${organization?.Address?.Street
                        } ${organization?.Address?.StreetNumber}`
                    }
                />
                <InformationLine
                    label={tReInspections('controlpoints.constructionOffice.postalCodeAndCity.label')}
                    value={
                        `${organization?.Address?.PostalCode
                        } ${organization?.Address?.Locality}`
                    }
                />
            </div>
            <Field
                name="email"
                validators={{
                    onChange:
                        validators.editElectricalProjectOrganizationCommand.email,
                    onChangeAsync: patchField('email'),
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label
                        label={t(
                            'those-involved.client.form.general-email.label',
                        )}
                    >
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t(
                                'those-involved.client.form.general-email.placeholder',
                            )}

                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="telephoneNumber"
                validators={{
                    onChange:
                        validators.editElectricalProjectOrganizationCommand.telephoneNumber,
                    onChangeAsync: patchField('telephoneNumber'),
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label
                        label={t(
                            'those-involved.client.form.general-phone.label',
                        )}
                    >
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t(
                                'those-involved.client.form.general-phone.placeholder',
                            )}

                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <ContactPersonsList electricalProjectVersionId={params.projectVersionId} organization={organization} />
        </div>
    );
}

function Footer() {
    return (
        <Section.Footer>
            <FooterNavigation />
        </Section.Footer>
    );
}
